/**
 * A running match.
 *
 * Owns the wasm simulation, the renderer, the lockstep loop, and the UI, and
 * is the only place where they meet. Everything that changes game state goes
 * out as a command; nothing here mutates the simulation directly.
 */

import initWasm, {
  Sim,
  entity_stride,
  event_stride,
  projectile_stride,
  unit_cost,
  unit_footprint,
  unit_is_building,
  unit_name,
} from '../../wasm/rts_sim.js';

import { GameRenderer } from '../render/renderer.js';
import { setKindScales } from '../render/entities.js';
import { EffectsLayer } from '../render/effects.js';
import { InputController, type DragBox } from './input.js';
import { Lockstep } from './lockstep.js';
import { CommandBuffer } from './commands.js';
import { Hud } from '../ui/hud.js';
import { Overlay } from '../ui/overlay.js';
import { SkirmishAi, type AiOptions } from './ai.js';
import { LocalTransport } from '../net/local.js';
import type { Transport } from '../net/transport.js';
import type { MatchSettings } from '../net/protocol.js';
import {
  F_ID,
  F_KIND,
  F_OWNER,
  F_X,
  F_Y,
  KIND_COUNT,
  KIND_HARVESTER,
  NO_ENTITY,
  TILE_ORE,
} from './constants.js';

let wasmReady: Promise<{ memory: WebAssembly.Memory }> | null = null;

/** Loads the wasm module once per page, no matter how many matches are played. */
export async function loadSim(): Promise<{ memory: WebAssembly.Memory }> {
  if (!wasmReady) {
    wasmReady = initWasm().then((mod: { memory: WebAssembly.Memory }) => ({
      memory: mod.memory,
    }));
  }
  return wasmReady;
}

export interface MatchOptions {
  container: HTMLElement;
  canvas: HTMLCanvasElement;
  transport: Transport;
  /** Present only for skirmish; null in multiplayer. */
  ai: { options: AiOptions; players: number[] } | null;
  onExit(): void;
}

export class MatchRuntime {
  private sim!: Sim;
  private renderer!: GameRenderer;
  private input!: InputController;
  private hud!: Hud;
  private overlay!: Overlay;
  private lockstep!: Lockstep;
  private memory!: WebAssembly.Memory;

  private stride = 10;
  private projStride = 4;
  private evtStride = 4;

  /** This tick's entity data, copied out of wasm so it survives memory growth. */
  private curr = new Float32Array(0);
  private currCount = 0;
  /** Previous tick's positions, keyed by entity id, for interpolation. */
  private prev = new Map<number, [number, number]>();

  private selection = new Set<number>();
  private controlGroups = new Map<number, number[]>();
  private pending = new CommandBuffer();
  private ais: SkirmishAi[] = [];
  private localPlayer = 0;

  private running = false;
  private paused = false;
  private lastFrame = 0;
  private rafHandle = 0;
  private tilesDirtyTimer = 0;
  private ended = false;

  constructor(private opts: MatchOptions) {}

  async start(): Promise<void> {
    const wasm = await loadSim();
    this.memory = wasm.memory;

    const settings = this.opts.transport.settings;
    this.localPlayer = this.opts.transport.playerIndex;

    this.stride = entity_stride();
    this.projStride = projectile_stride();
    this.evtStride = event_stride();

    this.sim = new Sim(
      settings.mapWidth,
      settings.mapHeight,
      settings.playerCount,
      settings.seedLo,
      settings.seedHi,
    );

    this.renderer = await GameRenderer.create(this.opts.canvas);

    const footprints = new Uint8Array(KIND_COUNT);
    for (let k = 0; k < KIND_COUNT; k++) footprints[k] = unit_footprint(k);
    setKindScales(footprints);

    const tiles = this.sim.tiles();
    this.renderer.setMap(settings.mapWidth, settings.mapHeight, tiles);

    this.overlay = new Overlay(this.opts.container);
    this.hud = new Hud(this.opts.container, (k) => unit_cost(k), {
      onProduce: (kind) => this.produce(kind),
      onCancel: (kind) => this.pending.cancel(this.localPlayer, kind),
      onMinimapJump: (x, y) => this.input.centerOn(x, y),
    });
    this.hud.setMap(settings.mapWidth, settings.mapHeight, tiles);

    this.input = new InputController(this.opts.canvas, this.renderer, {
      onSelect: (box, additive) => this.selectBox(box, additive),
      onClick: (world, additive) => this.clickSelect(world, additive),
      onOrder: (world, attackMove) => this.issueOrder(world, attackMove),
      onPlace: (tile) => this.placeStructure(tile),
      onCancelPlacement: () => {
        this.input.placementActive = false;
      },
      onStop: () => {
        if (this.selection.size) this.pending.stop(this.localPlayer, [...this.selection]);
      },
      onControlGroup: (index, set) => this.controlGroup(index, set),
      onProduce: (kind) => this.produce(kind),
      onTogglePause: () => this.togglePause(),
    });

    // Skirmish AI drives the other slots through the same command pipeline.
    if (this.opts.ai) {
      for (const p of this.opts.ai.players) {
        this.ais.push(new SkirmishAi(p, this.opts.ai.options));
      }
      const local = this.opts.transport as LocalTransport;
      local.setAiProvider((turn, player) => {
        const ai = this.ais.find((a) => (a as unknown as { player: number }).player === player);
        return ai ? ai.commands(turn, this.sim, this.memory, this.stride) : new Uint8Array(0);
      });
    }

    this.lockstep = new Lockstep(this.opts.transport, {
      simulate: (_turn, commands) => this.simulate(commands),
      collectCommands: () => this.pending.flush(),
      checksum: () => this.sim.checksum(),
      onStallChanged: (stalled) => {
        if (stalled) this.hud.setStatus('Waiting for other players...', 'warn');
        else this.hud.setStatus('Connected', 'ok');
      },
    });

    this.opts.transport.onFatal((reason) => this.fail(reason));

    // Centre on the local player's base rather than the map middle.
    this.snapshot();
    this.focusOnBase();

    const now = performance.now();
    this.lastFrame = now;
    this.lockstep.start(now);
    this.running = true;
    this.hud.setStatus('Connected', 'ok');
    window.addEventListener('resize', this.handleResize);
    this.loop(now);
  }

  private handleResize = (): void => {
    this.renderer.resize();
    this.overlay.resize();
    this.renderer.applyCamera();
  };

  // ------------------------------------------------------------- simulation

  private simulate(commands: Uint8Array): void {
    // Roll the current snapshot into `prev` before advancing, so the renderer
    // always has both endpoints to interpolate between.
    this.prev.clear();
    for (let i = 0; i < this.currCount; i++) {
      const o = i * this.stride;
      this.prev.set(this.curr[o + F_ID], [this.curr[o + F_X], this.curr[o + F_Y]]);
    }

    this.sim.step(commands);
    this.snapshot();
    this.pruneSelection();

    if (this.sim.game_over() && !this.ended) {
      this.ended = true;
      const winner = this.sim.winner();
      if (winner === this.localPlayer) {
        this.hud.showBanner('Victory', 'All enemy construction yards destroyed.');
      } else if (winner < 0) {
        this.hud.showBanner('Draw', 'No commanders remain.');
      } else {
        this.hud.showBanner('Defeat', 'Your construction yard was destroyed.');
      }
    }
  }

  /** Copies the render view out of wasm memory into JS-owned buffers. */
  private snapshot(): void {
    this.sim.build_view();
    this.currCount = this.sim.entity_count();
    const need = this.currCount * this.stride;
    if (this.curr.length < need) this.curr = new Float32Array(need);
    if (need > 0) {
      // A fresh view each time: wasm memory can be reallocated as it grows,
      // which would leave any cached typed array detached.
      const src = new Float32Array(this.memory.buffer, this.sim.entity_ptr(), need);
      this.curr.set(src);
    }
  }

  private pruneSelection(): void {
    for (const id of [...this.selection]) {
      if (!this.sim.is_alive(id)) this.selection.delete(id);
    }
    this.sim.set_selection(new Uint32Array([...this.selection]));
  }

  // ------------------------------------------------------------------ input

  private selectBox(box: DragBox, additive: boolean): void {
    const ids = this.sim.select_in_rect(this.localPlayer, box.x0, box.y0, box.x1, box.y1);
    if (!additive) this.selection.clear();
    for (const id of ids) this.selection.add(id);
    this.pruneSelection();
    this.updateSelectionInfo();
  }

  private clickSelect(world: { x: number; y: number }, additive: boolean): void {
    const id = this.sim.pick(world.x, world.y);
    if (!additive) this.selection.clear();
    if (id !== NO_ENTITY && this.sim.entity_owner(id) === this.localPlayer) {
      this.selection.add(id);
    }
    this.pruneSelection();
    this.updateSelectionInfo();
  }

  private issueOrder(world: { x: number; y: number }, attackMove: boolean): void {
    if (this.selection.size === 0) return;
    const ids = [...this.selection];
    const target = this.sim.pick(world.x, world.y);

    if (target !== NO_ENTITY) {
      const owner = this.sim.entity_owner(target);
      if (owner >= 0 && owner !== this.localPlayer) {
        this.pending.attack(this.localPlayer, ids, target);
        this.updateSelectionInfo();
        return;
      }
    }

    // Right-clicking ore sends harvesters to work and everyone else to move.
    const tx = Math.floor(world.x);
    const ty = Math.floor(world.y);
    if (this.sim.tile_at(tx, ty) === TILE_ORE) {
      const harvesters = ids.filter((id) => this.sim.entity_kind(id) === KIND_HARVESTER);
      const others = ids.filter((id) => this.sim.entity_kind(id) !== KIND_HARVESTER);
      if (harvesters.length) this.pending.harvest(this.localPlayer, harvesters, tx, ty);
      if (others.length) this.pending.move(this.localPlayer, others, world.x, world.y, attackMove);
      return;
    }

    this.pending.move(this.localPlayer, ids, world.x, world.y, attackMove);
  }

  private produce(kind: number): void {
    // If this structure is already built and waiting, the click means "place
    // it" rather than "queue another".
    if (unit_is_building(kind) && this.sim.pending_structure(this.localPlayer) === kind) {
      this.input.placementActive = true;
      return;
    }
    this.pending.produce(this.localPlayer, kind);
  }

  private placeStructure(tile: { x: number; y: number }): void {
    const kind = this.sim.pending_structure(this.localPlayer);
    if (kind === 255) {
      this.input.placementActive = false;
      return;
    }
    if (!this.sim.can_place(this.localPlayer, kind, tile.x, tile.y)) return;
    this.pending.place(this.localPlayer, kind, tile.x, tile.y);
    this.input.placementActive = false;
  }

  private controlGroup(index: number, assign: boolean): void {
    if (assign) {
      this.controlGroups.set(index, [...this.selection]);
      return;
    }
    const group = this.controlGroups.get(index);
    if (!group?.length) return;
    this.selection.clear();
    for (const id of group) {
      if (this.sim.is_alive(id)) this.selection.add(id);
    }
    this.pruneSelection();
    this.updateSelectionInfo();

    // Recalling a group also centres the camera on it.
    let sx = 0;
    let sy = 0;
    let n = 0;
    for (let i = 0; i < this.currCount; i++) {
      const o = i * this.stride;
      if (this.selection.has(this.curr[o + F_ID])) {
        sx += this.curr[o + F_X];
        sy += this.curr[o + F_Y];
        n++;
      }
    }
    if (n > 0) this.input.centerOn(sx / n, sy / n);
  }

  private updateSelectionInfo(): void {
    if (this.selection.size === 0) {
      this.hud.setSelection('');
      return;
    }
    const counts = new Map<number, number>();
    for (let i = 0; i < this.currCount; i++) {
      const o = i * this.stride;
      const id = this.curr[o + F_ID];
      if (!this.selection.has(id)) continue;
      const kind = this.curr[o + F_KIND] | 0;
      counts.set(kind, (counts.get(kind) ?? 0) + 1);
    }
    const parts = [...counts.entries()].map(([kind, n]) =>
      n > 1 ? `${n} x ${unit_name(kind)}` : unit_name(kind),
    );
    this.hud.setSelection(parts.join('   '));
  }

  private focusOnBase(): void {
    for (let i = 0; i < this.currCount; i++) {
      const o = i * this.stride;
      if ((this.curr[o + F_OWNER] | 0) === this.localPlayer && unit_is_building(this.curr[o + F_KIND] | 0)) {
        this.renderer.cam.x = this.curr[o + F_X];
        this.renderer.cam.y = this.curr[o + F_Y];
        this.renderer.applyCamera();
        return;
      }
    }
  }

  private togglePause(): void {
    // Pausing is single-player only: in a networked match one client halting
    // its own clock would just stall everyone else at the turn barrier.
    if (this.ais.length === 0) return;
    this.paused = !this.paused;
    this.hud.setStatus(this.paused ? 'Paused' : 'Connected', this.paused ? 'warn' : 'ok');
  }

  private fail(reason: string): void {
    this.running = false;
    this.hud.setStatus('Disconnected', 'bad');
    this.hud.showBanner('Match ended', reason);
  }

  // ------------------------------------------------------------------- loop

  private loop = (now: number): void => {
    if (!this.running) return;
    this.rafHandle = requestAnimationFrame(this.loop);

    const dtMs = Math.min(100, now - this.lastFrame);
    this.lastFrame = now;
    const dt = dtMs / 1000;

    this.input.updateCamera(dt);

    const alpha = this.paused ? 0 : this.lockstep.update(now, dtMs);

    // Terrain only changes as ore is mined, so re-reading it a few times a
    // second is plenty and avoids copying 16k bytes every frame.
    this.tilesDirtyTimer += dt;
    if (this.tilesDirtyTimer > 0.5) {
      this.tilesDirtyTimer = 0;
      this.renderer.terrain.syncTiles(this.sim.tiles());
    }

    this.renderer.entities.update(
      this.curr,
      this.prev,
      this.currCount,
      this.stride,
      alpha,
      this.input.isHeld('AltLeft') || this.input.isHeld('AltRight'),
    );

    this.updateEffects(dt);
    this.renderer.render();
    this.drawOverlay();
    this.updateHud();
  };

  private updateEffects(dt: number): void {
    const effects: EffectsLayer = this.renderer.effects;

    const evCount = this.sim.event_count();
    if (evCount > 0) {
      const events = new Float32Array(this.memory.buffer, this.sim.event_ptr(), evCount * this.evtStride);
      effects.consumeEvents(events, evCount);
    }

    const pCount = this.sim.projectile_count();
    const projectiles =
      pCount > 0
        ? new Float32Array(this.memory.buffer, this.sim.projectile_ptr(), pCount * this.projStride)
        : new Float32Array(0);
    effects.update(dt, projectiles, pCount, this.projStride);
  }

  private drawOverlay(): void {
    this.overlay.clear();
    const box = this.input.selectionBox;
    if (box) this.overlay.drawSelectionBox(box);

    if (this.input.placementActive) {
      const kind = this.sim.pending_structure(this.localPlayer);
      if (kind === 255) {
        this.input.placementActive = false;
        return;
      }
      const w = this.input.pointerWorld;
      const tx = Math.floor(w.x);
      const ty = Math.floor(w.y);
      this.overlay.drawPlacementGhost(
        this.renderer,
        tx,
        ty,
        unit_footprint(kind),
        this.sim.can_place(this.localPlayer, kind, tx, ty),
      );
    }
  }

  private updateHud(): void {
    const credits = this.sim.credits(this.localPlayer);
    this.hud.setCredits(credits);
    this.hud.updateBuildPanel(
      credits,
      (q) => this.sim.queue_items(this.localPlayer, q),
      (q) => this.sim.queue_progress(this.localPlayer, q),
      this.sim.pending_structure(this.localPlayer),
    );

    const cam = this.renderer.cam;
    const canvas = this.opts.canvas;
    const aspect = canvas.clientWidth / Math.max(1, canvas.clientHeight);
    this.hud.drawMinimap(this.curr, this.currCount, this.stride, this.localPlayer, {
      x: cam.x,
      y: cam.y,
      halfW: (cam.zoom / 2) * aspect,
      halfH: cam.zoom / 2,
    });
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafHandle);
    window.removeEventListener('resize', this.handleResize);
    this.lockstep?.stop();
    this.opts.transport.close();
    this.renderer?.dispose();
    this.hud?.root.remove();
    this.overlay?.canvas.remove();
    this.sim?.free();
    this.opts.onExit();
  }
}

/** Builds the settings a local skirmish runs with. */
export function skirmishSettings(seed = Date.now()): MatchSettings {
  return {
    seedLo: seed >>> 0,
    seedHi: Math.floor(seed / 2 ** 32) >>> 0,
    mapWidth: 128,
    mapHeight: 128,
    playerCount: 2,
    commandDelay: 2,
  };
}
